//@flow
import FunctionStore from '../Base/functionStoreClass';
import Constants from '../../constants';
import Heap from '../../Utils/heap-interface';
import TrackJs from '../../Utils/trackjs-interface';
import Facebook from '../../Utils/fb-pixel-interface';
import GoogleTagManager from '../../Utils/gtm-interface';
import Ire from '../../Utils/ire-interface';
import CookiesData from '../../Utils/cookies-data';
import FB from '../../Utils/fb-interface';
import Events from './events';
import Flatten from 'flat';
import FriendbuyV2 from '../../Utils/friendbuyV2';
import NumberUtils from '../../Utils/number';
import FrameLoader from '../../Utils/frameLoader';
import _ from 'underscore';
import GiftUtils from '../../Utils/gifts';
import Sailthru from '../../Utils/sailthru';
import UrlData from '../../Utils/url-data';
import OrderItemModel from '../Order/orderItemModel';
import QuoteItemModel from '../Quote/quoteItemModel';

import typeof CustomerStore from '../Customer/customer';
import typeof QuoteStore from '../Quote';
import typeof ProductsStore from '../Products/products';
import typeof MembershipStore from '../Membership/membershipStore';
import typeof OrdersStore from '../Order/orderSingleton';
import typeof SessionStore from '../Session/session';
import typeof CategoriesStore from '../MemberCategories/categories';
import type CustomerModel from '../Customer/customerModel';
import type MembershipModel from '../Membership/membershipModel';
import type { Product } from '../../Utils/gtm-interface';
import type { Error, HandlerAdd, HandlerFunction } from '../Base/functionStoreClass';
import type { Items as SailthruItems } from '../../Utils/sailthru';

type Stores = {
  QuoteStore:QuoteStore,
  CustomerStore:CustomerStore,
  ProductsStore:ProductsStore,
  MembershipStore:MembershipStore,
  OrdersStore:OrdersStore,
  SessionStore:SessionStore,
  CategoriesStore:CategoriesStore,
};

export default class HeapStore extends FunctionStore {
  stores:Stores

  eventProperties:{
    loggedIn:boolean,
    hasActiveMembership:boolean,
    hasActiveSub:boolean,
    isGuest:boolean,
  }

  constructor(dependencyStores:Stores) {
    super({}, dependencyStores);
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.GET_CUSTOMER_SUCCESS, _identifyUser);
    onSuccess(Constants.ADD_QUOTE_ITEM, [ _trackAddCartItem, _trackAddCartItemFB, _trackAddCartItemGTM ]);
    onSuccess(Constants.UPDATE_QUOTE_ITEM_QTY, [ _trackUpdateCartItem, _trackAddCartItemGTM ]);
    onSuccess(Constants.DELETE_QUOTE_ITEM, [ _trackDeleteCartItem, _trackDeleteCartItemGTM ]);
    onSuccess(Constants.UPDATE_QUOTE_SUCCESS, [ _trackCartSailthru, _trackEvent(Events.QUOTE_UPDATED) ]);
    onSuccess(Constants.HEAP_TRACK_SIGN_UP, _trackSignUp);
    onSuccess(Constants.CREATE_CUSTOMER, _trackSignUp);
    onSuccess(Constants.CREATE_CUSTOMER_SUCCESS, [ _trackEvent(Events.SIGN_UP_SUCCESS), _trackSignUpSuccess ]);
    onSuccess(Constants.LOGIN, [ _trackEvent(Events.SIGN_IN_SUCCESS) ]);
    onSuccess(Constants.LOGIN_PENDING, _trackSignIn);
    onSuccess(Constants.HEAP_TRACK_CHECKOUT_STEP_ONE, _trackEvent(Events.CHECKOUT_STEP_ONE));
    onSuccess(Constants.HEAP_TRACK_CHECKOUT_STEP_TWO, _trackCheckoutStepTwo);
    onSuccess(Constants.HEAP_TRACK_CHECKOUT_STEP_THREE, _trackCheckoutStepThree);
    onSuccess(Constants.CREATE_ORDER_SUCCESS,
      [_trackOrderSuccess, _trackOrderSuccessGTM, _trackOrderSuccessFB, _trackOrderSuccessIre]);
    onSuccess(Constants.HEAP_TRACK_CART_GO_TO_CHECKOUT, _trackCartToCheckout);
    onSuccess(Constants.HEAP_TRACK_CANCEL_SURVEY, _trackCancelSurvey);
    onSuccess(Constants.HEAP_TRACK_EMAIL_CAPTURE, _trackEmailCapture);
    onSuccess(Constants.HEAP_TRACK_PAGE_PERFORMANCE, _trackPagePerformance);

    onError(Constants.CREATE_ORDER_SUCCESS, _trackEvent(Events.ORDER_ERROR));
  }

  __onDispatchSuccess(action:string, data:Object) {
    try {
      this._waitForStores();
      this._updateEventProperties();

      super.__onDispatchSuccess(action, data);
    } catch(e) {
      TrackJs.console.debug(`error: ${e}`);
      TrackJs.console.debug(`success action: ${action}`);
      TrackJs.track(`Heap store error`);
    }
  }

  __onDispatchError(action:string, error:?Error, data:Object) {
    try {
      this._trackErrors(action, data);
      super.__onDispatchError(action, error, data);
    } catch(e) {
      TrackJs.console.debug(`error: ${e}`);
      TrackJs.console.debug(`error action: ${action}`);
      TrackJs.track(`Heap store error`);
    }
  }

  _getCustomer():CustomerModel {
    return this.stores.CustomerStore.getCustomer();
  }

  _getMembership():MembershipModel {
    return this.stores.MembershipStore.getMembership();
  }

  _trackErrors(action:string, data:Object) {
    // if there is no information of the request don't track.
    if (!data.response || !data.response.req) return;
    const errors = data.getErrors().join(", ");

    Heap.track(Events.API_ERROR, {
      message: errors,
      url: data.getTrackingUrl(),
      method: data.getRequestMethod(),
    });
  }

  _updateEventProperties() {
    const customer = this._getCustomer();
    const newEventsProps = Flatten({
      loggedIn: this.stores.SessionStore.isLoggedIn(),
      hasActiveMembership: customer.hasActiveMembership(),
      isGuest: customer.isGuest(),
      hasActiveSub: Boolean(this.stores.QuoteStore.getSubActiveQuote().getId()),
      quote: this._getQuoteTrackingData(),
    });

    if (_.isEqual(newEventsProps, this.eventProperties)) return;
    Heap.addEventProperties(newEventsProps);
    this.eventProperties = newEventsProps;
  }

  _getQuoteTrackingData() {
    const quote = this.stores.QuoteStore.getQuote();
    if (!quote.getShippingAddressId()) return null;

    const address = this.stores.CustomerStore.getAddresses()
      .findById(quote.getShippingAddressId());
    if (!address) return null;

    return {
      address: {
        city: address.getCity(),
        region: address.getRegion(),
        country: address.getCountryId(),
      },
    };
  }

  _getOrderSuccessProperties(eventProperties:Object = {}) {
    try {
      const order = this.stores.OrdersStore.getOrder();
      const orderItems = order.getOrderItems();
      const categories = this.stores.CategoriesStore.getCategories();
      const is_gift = categories.hasOrderItemForUrlKey('m-gifts', orderItems) ||
      (orderItems && orderItems.some(item => item.sku === GiftUtils.EGIFT_SKU));

      eventProperties.order = order.getTrackingData();
      eventProperties.order.newUser = this._isNewUser();
      eventProperties.order.is_gift = is_gift;
      return Flatten(eventProperties);
    } catch(e) {
      TrackJs.console.debug(`error: ${e}`);
      TrackJs.track('Heap unable to retrieve properties order success');
    }
  }

  _getTotalsProps() {
    const quoteId = this.stores.QuoteStore.getQuote().getId();
    const products = this.stores.ProductsStore.getProducts();
    const quoteItems = this.stores.QuoteStore.getQuoteItems(quoteId);
    const quoteTotals = this.stores.QuoteStore.getQuoteTotals(quoteId, {
      products,
      quoteItems,
    });

    return {
      quoteId,
      grandTotal: quoteTotals.getGrandTotal(),
      subtotal: quoteTotals.getSubtotal(),
      discount: quoteTotals.getDiscount(),
      savings: quoteTotals.getSavings(),
      discountName: quoteTotals.getDiscountName(),
      shipping: quoteTotals.getShipping(),
    };
  }

  _getCartItems() {
    const quoteId = this.stores.QuoteStore.getQuote().getId();
    const quoteItems = this.stores.QuoteStore.getQuoteItems(quoteId).models;

    const productPrices = quoteItems.map((item) => {
      return this._getProdProperties(item.getSku()).productPrice || '';
    }).join(',');

    const items = {
      productPrices,
      skus: quoteItems.map(item => item.getSku()).join(','),
      qty: quoteItems.map(item => item.getQty()).join(','),
      prices: quoteItems.map(item => item.getPrice()).join(','),
    };

    return items;
  }

  _getCartItemsDataForSailthru(items:Array<OrderItemModel|QuoteItemModel>):SailthruItems{
    const itemsData = [];
    const baseUrl =  UrlData.getOrigin();
    items.forEach((item) => {
      const product = this._getProduct(item.getSku());
      if (!product) return;
      const price = item.getPrice();

      itemsData.push({
        qty: item.getQty(),
        title: product.getName(),
        id: item.getSku(),
        price: price ? Math.trunc(price*100) : 0,
        url: baseUrl + product.getUrl(false),
        vars: {
          image: baseUrl + product.getSnackcardImageUrl(300, 235),
          url: baseUrl + product.getUrl(false),
        },
      });
    });

    return itemsData;
  }

  _isNewUser() {
    const membership = this._getMembership();
    return !membership.isReturningMember();
  }

  _getProduct(sku:string) {
    const productsStore = this.stores.ProductsStore;
    return productsStore.getProducts().getProductBySku(sku);
  }

  _getProdProperties(sku:string) {
    const product = this._getProduct(sku);
    if (product) {
      return {
        productPrice: product.getMemberPrice(),
        productName: product.getName(),
        productImage: product.getSnackcardImageUrl(300, 235),
        productMsrp: product.getMsrp(),
      };
    }
    return {};
  }

  _waitForStores() {
    const storeTokens = Object.keys(this.stores).map((storeKey) => {
      return this.stores[storeKey].getDispatchToken();
    });

    this.getDispatcher().waitFor(storeTokens);
  }
}

function _trackEvent(eventName:string):HandlerFunction {
  return function() {
    Heap.track(eventName);
  };
}

function _trackCustomerWithFriendbuy(customer:CustomerModel) {
  const id = customer.getId();
  const email = customer.getEmail();
  const first_name = customer.getFirstname();
  const last_name = customer.getLastname();

  FriendbuyV2.track('customer', { id, email, first_name, last_name });
}

function _identifyUser(store:HeapStore) {
  const customer = store._getCustomer();
  const membership = store._getMembership();
  let email = customer.getEmail();

  /*
    Identifies user on heap and sailthru
  */
  Heap.identify(email);
  TrackJs.configure({ userId: email });

  // Tracks logged-in customers with friendbuy
  _trackCustomerWithFriendbuy(customer);

  email = (email && email.toLowerCase().trim()) || '';
  GoogleTagManager.generalEvent({ event: 'identify', customer: { email } });

  Heap.addUserProperties({
    isFBUser: customer.isFBUser(),
    customerId: customer.getId(),
    membershipId: membership.getId(),
  });

  Sailthru.userSignUp(email);

  Ire.identify(customer.getId(), customer.getEmail());
}

type CartAddData = {
  sku:string,
  qty:number,
};

function _trackAddCartItem(store:HeapStore, data:CartAddData) {
  const eventProperties:Object = {
    productSku: data.sku,
    qty: data.qty,
  };

  Object.assign(eventProperties, store._getProdProperties(data.sku));
  Heap.track(Events.ADD_ITEM_TO_CART, eventProperties);
  FB.AppEvents.logEvent(Events.ADD_ITEM_TO_CART, null, eventProperties);
}

function _trackAddCartItemFB(store:HeapStore, data:CartAddData) {
  const product = store._getProduct(data.sku);
  const eventProperties:Object = {
    value: 0,
    currency: 'USD',
    content_type: 'product',
    content_ids: [ data.sku ],
  };

  if (product) {
    eventProperties.value = product.getMemberPrice();
    eventProperties.content_name = product.getName();
  }

  Facebook.track('AddToCart', eventProperties);
}

function _trackAddCartItemGTM(store:HeapStore, data:CartAddData) {
  const product = store._getProduct(data.sku);
  if (!product) return;

  const gtmProduct = GoogleTagManager.buildProductData(product);
  gtmProduct.quantity = data.qty;

  GoogleTagManager.ecommerceEvent({
    add: {
      products:[ gtmProduct ],
    },
  }, 'addToCart');
}

function _trackCartSailthru(store:HeapStore) {
  const customer = store._getCustomer();
  const email = customer.getEmail();
  if (!email) return;

  const quoteId = this.stores.QuoteStore.getQuote().getId();
  const quoteItems = this.stores.QuoteStore.getQuoteItems(quoteId).models;
  const items = store._getCartItemsDataForSailthru(quoteItems);

  if (!items.length) return;

  const data = {
    items,
    email,
    incomplete: 1,
  };

  if (this._lastSailthruData && _.isEqual(this._lastSailthruData, data)) return;

  Sailthru.addToCart(data);
  this._lastSailthruData = data;
}

function _trackUpdateCartItem(store:HeapStore, data:{
  sku:string,
  oldQty:number,
  qty:number,
  itemId:string,
}) {
  const eventProperties:Object = {
    productSku: data.sku,
    oldQty: data.oldQty,
    qty: data.qty,
    quoteItemId: data.itemId,
  };

  Object.assign(eventProperties, store._getProdProperties(data.sku));

  Heap.track(Events.UPDATE_CART_ITEM, eventProperties);
  FB.AppEvents.logEvent(Events.UPDATE_CART_ITEM, null, eventProperties);
}

function _trackDeleteCartItem(store:HeapStore, data:{
  sku:string,
  oldQty:number,
  itemId:string,
}) {
  const eventProperties:Object = {
    quoteItemId: data.itemId,
    qty: data.oldQty,
    productSku: data.sku,
  };

  Object.assign(eventProperties, store._getProdProperties(data.sku));

  Heap.track(Events.DELETE_CART_ITEM, eventProperties);
  FB.AppEvents.logEvent(Events.DELETE_CART_ITEM, null, eventProperties);
}

function _trackDeleteCartItemGTM(store:HeapStore, data:{
  sku:string,
  oldQty:number,
  itemId:string,
}) {
  const product = store._getProduct(data.sku);
  if (!product) return;

  const gtmProduct = GoogleTagManager.buildProductData(product);
  gtmProduct.quantity = data.oldQty;

  GoogleTagManager.ecommerceEvent({
    remove: {
      products:[ gtmProduct ],
    },
  }, 'removeFromCart');
}

function _trackSignUp(store:HeapStore, data:{
  firstname:string,
  lastname:string,
  email:string,
  how_heard_about_nb:string,
}) {
  const eventProperties = {
    firstName: data.firstname,
    lastName: data.lastname,
    email: data.email,
    howDidYouHear: data.how_heard_about_nb,
  };

  Heap.track(Events.SIGN_UP, eventProperties);
}

function _trackSignUpSuccess(store:HeapStore) {
  const customer = store.stores.CustomerStore.getCustomer();
  FriendbuyV2.track('sign_up', {
    id: customer.getId(),
    email: customer.getEmail(),
    name: customer.getFirstname(),
  });
}

function _trackSignIn(store:HeapStore, data:{ username:string }) {
  Heap.track(Events.SIGN_IN, { email: data.username });
}

function _trackCheckoutStepTwo(store:HeapStore, data:Object) {
  const eventProperties = {
    how_heard_about_nb: data.how_heard_about_nb,
  };

  Heap.track(Events.CHECKOUT_STEP_TWO, eventProperties);
}

function _trackCheckoutStepThree(store:HeapStore, data:Object) {
  let eventProperties = {};
  try {
    const quote = store.stores.QuoteStore.getQuote();
    eventProperties.isNewUser = store._isNewUser();
    eventProperties.cartItems = store._getCartItems();
    eventProperties.quoteId = data.quoteId;
    eventProperties.subscription = quote.isSubscription();
    Object.assign(eventProperties, store._getTotalsProps());
    eventProperties = Flatten(eventProperties);
  } catch(e) {
    TrackJs.console.debug(`error: ${e}`);
    TrackJs.track('Heap unable to retrieve properties third step checkout');
  }
  Heap.track(Events.CHECKOUT_STEP_THREE, eventProperties);
}

function _trackOrderSuccess(store:HeapStore) {
  const eventProperties = store._getOrderSuccessProperties();
  Heap.track(Events.ORDER_SUCCESS, eventProperties);
  FB.AppEvents.logEvent(Events.ORDER_SUCCESS, null, eventProperties);

  // for some reason, google thinks global-wide is malware, so we have to directly
  // insert the tag.  The tag uses the order id in case there are multiple orders
  // in the same session
  FrameLoader.addFrame('gw-coversion-1', 'https://convert.ctypy.com/aff_l?offer_id=10432');
  FrameLoader.addFrame('gw-coversion-2', 'https://a.vghjc.com/p.ashx?o=46979');
}

function _trackOrderSuccessGTM(store:HeapStore) {
  // since it's just one data object, populate the event name and page here
  const eventProperties:any = store._getOrderSuccessProperties({
    'event': 'success',
    'page': 'checkout',
  });
  // do the GA order success event
  GoogleTagManager.generalEvent(eventProperties);

  const order = store.stores.OrdersStore.getOrder();
  if (!order) return;

  const items = order.getOrderItems();
  const purchases:Product[] = items.map((item) => {
    return {
      name: item.name,
      id: item.sku,
      price: item.price.toFixed(2),
      quantity: parseFloat(item.qty_ordered),
    };
  });

  const ecommerce = {
    purchase: {
      actionField: {
        id: Number(order.getId() || 0).toString(),
        // GTM/Google Ads require conversion value to be a number
        grandTotal: order.getGrandTotal(),
        revenue: NumberUtils.toFixed(order.getGrandTotal()),
        tax: NumberUtils.toFixed(order.getTaxAmount()),
        shipping: NumberUtils.toFixed(order.getShippingAmount()),
        coupon: order.getCouponCode(),
      },
      products: purchases,
    },
  };

  // to the enhanced ecommerce purchase event
  GoogleTagManager.ecommerceEvent(ecommerce, 'purchase');
}

function _trackOrderSuccessFB(store:HeapStore) {
  const eventProperties = {
    value: 0,
    currency: 'USD',
    content_type: 'product',
    content_ids: [ ],
  };

  const order = store.stores.OrdersStore.getOrder();
  if (order) {
    eventProperties.value = order.getSubtotal() || 0;
    const items = order.getOrderItems();
    eventProperties.content_ids = items.map( (item) => item.sku );
  }

  Facebook.track('Purchase', eventProperties);
}

function _trackOrderSuccessIre(store:HeapStore) {
  const order = store.stores.OrdersStore.getOrder();
  // Check if customer has orders in addition to this order.
  // If so consider them a returning customer
  const orders = store.stores.OrdersStore.getOrders();
  const otherOrders = orders.models.filter(o => o.getId() !== order.getId());
  const customerStatus = otherOrders.length ? 'returning' : 'new';

  const customer = store.stores.CustomerStore.getCustomer();
  if (!(order && customer)) {
    TrackJs.track('_trackOrderSuccessIre error: order or customer not available');
    return;
  }
  const items = order.getOrderItems()
    .map(orderItem => {
      const item = new OrderItemModel(orderItem);
      return {
        subTotal: item.getPrice() || 0,
        category: 'Products',
        sku: item.getSku(),
        quantity: item.getQty(),
      };
    });

  const clickId = CookiesData.get('ireClickId');
  // Make sure discount param is a positive number
  let orderDiscount = order.getDiscountAmount() || 0;
  if (orderDiscount < 0) {
    orderDiscount = orderDiscount * -1;
  }

  const data = {
    orderId: order.getId() || '',
    clickId,
    customerId: customer.getId(),
    customerStatus,
    orderPromoCode: order.getCouponCode() || '',
    orderDiscount,
    currencyCode: 'USD',
    items,
  };

  Ire.trackConversation(data);

  Ire.addShoppersScript(order.getId() || '', order.getGrandTotal() || 0);
}

function _trackCartToCheckout(store:HeapStore) {
  let eventProperties = {};
  try {
    eventProperties.isNewUser = store._isNewUser();
    eventProperties.cartItems = store._getCartItems();
    Object.assign(eventProperties, store._getTotalsProps());
    eventProperties = Flatten(eventProperties);
  } catch(e) {
    TrackJs.console.debug(e);
    TrackJs.track('Heap unable to retrieve cart to checkout properties');
  }

  Heap.track(Events.CART_TO_CHECKOUT, eventProperties);
}

function _trackCancelSurvey(store:HeapStore, data:Object) {
  let eventProperties = {
    email: data.email,
    responses: data.responses,
  };
  eventProperties = Flatten(eventProperties);
  Heap.track(Events.CANCEL_SURVEY, eventProperties);
}

function _trackEmailCapture(store:HeapStore, data:Object) {
  Heap.track('emailCapture', data);
}

function _trackPagePerformance(store:HeapStore, data:Object) {
  Heap.track('pagePerformance', data);
}
